"use client"

/**
 * 共有 SSE hooks（frontend/shared/auth）。
 *
 * EventSource はカスタムヘッダを付けられないため接続URLに ?token= でトークンを載せる。
 * ネイティブ自動再接続は古いトークンを使い回すため、再接続のたびにトークンを取り直す。
 * 期限切れは getAuthToken(fetchAuthSession) が自動リフレッシュ。null なら refreshAuthToken(forceRefresh)
 * を試し、連続失敗が閾値に達したら handleApiAuthError（タイムアウトのポップアップ→ログイン）に切り替える。
 *
 * 認証モジュール(auth)とイベント種別は各アプリの hooks/*.ts ラッパから注入する。
 */

import { useEffect, useRef, useState, useCallback } from "react"
import type { AuthModule } from "./auth-core"
import { claimSession, getSessionStatus, type SessionApiClient } from "./session-api"

const SSE_RETRY_INTERVAL = 15000

// セッションタイムアウト(ポップアップ→ログイン)に切り替えるまでの、トークン取得連続失敗回数のしきい値。
// 起動直後(Amplify設定前)の一時的な未取得で誤ってログアウトさせないための猶予。
const MAX_TOKEN_FAILURES_BEFORE_TIMEOUT = 2

// cti_bridge の内容更新イベント配信エンドポイント（認証は ?token= クエリ）
const CONTENT_SSE_PATH = "/api/notification/events"
// 通知件数の配信エンドポイント
const NOTIFICATION_UPDATE_PATH = "/api/notification/update"

// 内容更新イベントの種別。バックエンド(common/pkg/eventbus/types.go)と一致させること。
export const CONTENT_EVENT = {
  cast: "cast.updated",
  driver: "driver.updated",
  staff: "staff.updated",
  dispatch: "dispatch.events_updated",
  chatMessage: "chat.message",
  driverLocation: "driver.location_updated",
  sessionSuperseded: "session.superseded",
} as const

// イベントペイロード（サーバ側 TargetPayload と対応）。宛先フィルタはサーバ側で実施済み。
export interface ContentEventPayload {
  roomId?: string
  resources?: string[]
  reason?: string
  app?: string // セッション制御イベントの対象アプリ('cast'|'driver'|'staff')
  deviceId?: string // セッション制御イベントで所有権を得た端末ID
}

type AuthForSse = Pick<AuthModule, "getAuthToken" | "refreshAuthToken" | "handleApiAuthError">

// SSE 接続用の最新トークンを取得する。取得不能が閾値回続いたら timedOut=true（=タイムアウト処理済み）。
// counterRef は連続失敗回数を跨ぎ保持する（起動直後の一時的未取得での誤発火を避ける）。
async function acquireToken(
  auth: AuthForSse,
  counterRef: { current: number },
): Promise<{ token: string | null; timedOut: boolean }> {
  let token = await auth.getAuthToken()
  if (!token) token = await auth.refreshAuthToken()
  if (!token) {
    counterRef.current++
    if (counterRef.current >= MAX_TOKEN_FAILURES_BEFORE_TIMEOUT) {
      auth.handleApiAuthError()
      return { token: null, timedOut: true }
    }
    return { token: null, timedOut: false }
  }
  counterRef.current = 0
  return { token, timedOut: false }
}

/**
 * cti_bridge の SSE に接続し、指定種別の内容更新イベントを購読する汎用 hook。
 * eventTypes が空の場合は接続しない（無駄な接続を作らない）。
 */
export function useContentEvents(
  auth: AuthModule,
  eventTypes: string[],
  onEvent: (type: string, payload: ContentEventPayload | null) => void,
) {
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  const typesRef = useRef(eventTypes)
  typesRef.current = eventTypes
  const typesKey = eventTypes.join(",")

  useEffect(() => {
    let eventSource: EventSource | null = null
    let retryTimeout: ReturnType<typeof setTimeout> | null = null
    let closed = false
    const tokenFailCounter = { current: 0 }

    const scheduleRetry = () => {
      if (closed) return
      retryTimeout = setTimeout(connect, SSE_RETRY_INTERVAL)
    }

    const connect = async () => {
      if (closed) return
      if (typesRef.current.length === 0) return // 購読対象が無ければ接続しない
      eventSource?.close()
      eventSource = null

      try {
        const { token, timedOut } = await acquireToken(auth, tokenFailCounter)
        if (closed || timedOut) return
        if (!token) {
          scheduleRetry()
          return
        }

        const baseUrl = process.env.NEXT_PUBLIC_API_URL ?? window.location.origin
        const url = `${baseUrl.replace(/\/$/, "")}${CONTENT_SSE_PATH}?token=${encodeURIComponent(token)}`
        const source = new EventSource(url)
        eventSource = source

        for (const type of typesRef.current) {
          source.addEventListener(type, (e: MessageEvent) => {
            let payload: ContentEventPayload | null = null
            try {
              payload = e.data ? JSON.parse(e.data) : null
            } catch {
              payload = null
            }
            onEventRef.current(type, payload)
          })
        }

        source.onerror = () => {
          source.close()
          if (eventSource === source) {
            eventSource = null
          }
          scheduleRetry()
        }
      } catch {
        scheduleRetry()
      }
    }

    connect()

    return () => {
      closed = true
      eventSource?.close()
      eventSource = null
      if (retryTimeout) {
        clearTimeout(retryTimeout)
      }
    }
    // typesKey が変わったときのみ再接続する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesKey])
}

export interface NotificationUpdate {
  notice: number
  task: number
  message: number
}

export interface UseNotificationSSEResult {
  noticeCount: number
  taskCount: number
  messageCount: number
  isConnected: boolean
}

/**
 * 新着通知件数を SSE で取得する hook。通信エラー時はリトライ間隔おきに再接続する。
 * contentEventTypes を渡すと、内容更新イベントでもバッヂ件数を即時更新する（cast 用。
 * 既定 [] では内容イベント購読しない＝staff/driver と同じ挙動）。
 */
export function useNotificationSSE(
  auth: AuthModule,
  contentEventTypes: string[] = [],
): UseNotificationSSEResult {
  const [noticeCount, setNoticeCount] = useState(0)
  const [taskCount, setTaskCount] = useState(0)
  const [messageCount, setMessageCount] = useState(0)
  const [isConnected, setIsConnected] = useState(false)

  const eventSourceRef = useRef<EventSource | null>(null)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tokenFailCounterRef = useRef({ current: 0 })

  // 内容更新イベントでバッヂ件数を即時更新する（5秒ゲート対象外。15秒の件数SSEが最終的に整合）。
  const handleContentEvent = useCallback((type: string, payload: ContentEventPayload | null) => {
    if (type === CONTENT_EVENT.chatMessage) {
      setMessageCount((prev) => prev + 1)
    } else if (payload?.resources?.includes("notification")) {
      setNoticeCount((prev) => prev + 1)
    }
  }, [])

  // contentEventTypes が空なら useContentEvents 側で接続しない（staff/driver は無接続）。
  useContentEvents(auth, contentEventTypes, handleContentEvent)

  const connect = useCallback(async () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }

    try {
      const { token, timedOut } = await acquireToken(auth, tokenFailCounterRef.current)
      if (timedOut) return
      if (!token) {
        retryTimeoutRef.current = setTimeout(() => {
          connect()
        }, SSE_RETRY_INTERVAL)
        return
      }

      const url = `${NOTIFICATION_UPDATE_PATH}?token=${encodeURIComponent(token)}`
      const eventSource = new EventSource(url)
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        setIsConnected(true)
      }

      eventSource.onmessage = (event) => {
        try {
          const data: NotificationUpdate = JSON.parse(event.data)
          setNoticeCount(data.notice)
          setTaskCount(data.task)
          setMessageCount(data.message)
        } catch {
          // パースエラーは無視
        }
      }

      eventSource.onerror = () => {
        setIsConnected(false)
        eventSource.close()
        eventSourceRef.current = null
        retryTimeoutRef.current = setTimeout(() => {
          connect()
        }, SSE_RETRY_INTERVAL)
      }
    } catch {
      setIsConnected(false)
      retryTimeoutRef.current = setTimeout(() => {
        connect()
      }, SSE_RETRY_INTERVAL)
    }
    // auth は安定参照（アプリ生成時に固定）のため依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    connect()
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
    }
  }, [connect])

  return { noticeCount, taskCount, messageCount, isConnected }
}

/**
 * 受付(hurray)連動による配車イベント変化を SSE で受信する hook（dispatch_sys 用）。
 * dispatch.events_updated 受信時に onUpdate を呼ぶ。
 */
export function useDispatchEvents(auth: AuthModule, onUpdate: () => void) {
  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate

  useEffect(() => {
    let eventSource: EventSource | null = null
    let retryTimeout: ReturnType<typeof setTimeout> | null = null
    let closed = false
    const tokenFailCounter = { current: 0 }

    const scheduleRetry = () => {
      if (closed) return
      retryTimeout = setTimeout(connect, SSE_RETRY_INTERVAL)
    }

    const connect = async () => {
      if (closed) return
      eventSource?.close()
      eventSource = null

      try {
        const { token, timedOut } = await acquireToken(auth, tokenFailCounter)
        if (closed || timedOut) return
        if (!token) {
          scheduleRetry()
          return
        }

        const baseUrl = process.env.NEXT_PUBLIC_API_URL ?? window.location.origin
        const url = `${baseUrl.replace(/\/$/, "")}${CONTENT_SSE_PATH}?token=${encodeURIComponent(token)}`
        const source = new EventSource(url)
        eventSource = source

        source.addEventListener(CONTENT_EVENT.dispatch, () => {
          onUpdateRef.current()
        })

        source.onerror = () => {
          source.close()
          if (eventSource === source) {
            eventSource = null
          }
          scheduleRetry()
        }
      } catch {
        scheduleRetry()
      }
    }

    connect()

    return () => {
      closed = true
      eventSource?.close()
      eventSource = null
      if (retryTimeout) {
        clearTimeout(retryTimeout)
      }
    }
  }, [])
}

/**
 * 複数端末同時ログインの締め出しガード（cast/driver/staff 用）。
 *
 * enabled(=ログイン確定)になったら session/claim でこのアプリの所有端末をこの端末に更新する。
 * 別端末が後からログイン(claim)すると session.superseded イベントが本人宛に届き、
 * それが「このアプリ かつ 別 deviceId」なら locked=true にする（呼び出し側で全画面ロック）。
 * 排他の単位はアプリ(app)ごとなので、driver スマホのログインで staff PC は締め出されない。
 *
 * 取りこぼし対策として、マウント時とウィンドウ復帰(focus/visibility)時に session/status で所有権を再確認する。
 *
 * @returns locked この端末が締め出されたか
 */
export function useSessionGuard(
  auth: AuthModule,
  api: SessionApiClient,
  app: string,
  deviceId: string,
  enabled: boolean,
): boolean {
  const [locked, setLocked] = useState(false)

  // 所有権を確認し、失っていれば locked にする。
  const verifyOwnership = useCallback(async () => {
    if (!deviceId) return
    try {
      const status = await getSessionStatus(api, app, deviceId)
      if (!status.isOwner) setLocked(true)
    } catch {
      // 確認失敗時は締め出さない（誤ロックを避ける。次の機会に再確認する）
    }
  }, [api, app, deviceId])

  // ログイン確定後: このアプリの所有端末をこの端末に確定し、直後に所有権を確認する。
  useEffect(() => {
    if (!enabled || !deviceId) return
    let cancelled = false
    ;(async () => {
      try {
        await claimSession(api, app, deviceId)
      } catch {
        // claim 失敗は致命的ではない（ownership 確認でフォロー）
      }
      if (!cancelled) await verifyOwnership()
    })()
    return () => {
      cancelled = true
    }
  }, [enabled, deviceId, app, api, verifyOwnership])

  // ウィンドウ復帰時に再確認（バックグラウンド中に superseded を取りこぼした端末を締め出す）。
  useEffect(() => {
    if (!enabled) return
    const onFocus = () => {
      if (document.visibilityState === "visible") verifyOwnership()
    }
    window.addEventListener("focus", onFocus)
    document.addEventListener("visibilitychange", onFocus)
    return () => {
      window.removeEventListener("focus", onFocus)
      document.removeEventListener("visibilitychange", onFocus)
    }
  }, [enabled, verifyOwnership])

  // SSE: session.superseded を購読し、このアプリ かつ 別端末なら締め出す。
  const handleEvent = useCallback(
    (type: string, payload: ContentEventPayload | null) => {
      if (type !== CONTENT_EVENT.sessionSuperseded) return
      if (payload?.app === app && payload.deviceId && payload.deviceId !== deviceId) {
        setLocked(true)
      }
    },
    [app, deviceId],
  )

  useContentEvents(auth, enabled ? [CONTENT_EVENT.sessionSuperseded] : [], handleEvent)

  return locked
}
